'use client';

// import { CustomImage as Image } from '..';
import { useLocale, useTranslations } from 'next-intl';
import { useGsapContext } from 'hooks/animation/useGsapContext';
import { useRef } from 'react';
import { useIsomorphicLayoutEffect } from 'hooks/dom/useIsomorphicLayoutEffect';
import { Container } from '..';
import { PLargeGradient } from './styleConstants';
import gsap from 'gsap';
import { CustomImage as Image } from '..';
import styles from './TestChartSection.module.css';
import { splitForStagger } from 'utils/splitWords';

export const TestChartSection = ({ className }: { className?: string }) => {
  const gsapContext = useGsapContext();
  const t = useTranslations('bloodVision');
  const locale = useLocale();

    const container = useRef<HTMLDivElement>(null);
    const videoRef = useRef<HTMLImageElement>(null);

    const animationInit = () => {
      if (!container.current || !window) return;

      gsapContext.add(() => {
        const tl = gsap.timeline({
          defaults: {
            duration: 1,
            ease: 'power2.easeInOut',
          },
          scrollTrigger: {
            trigger: container.current,
            start: 'top top',
            end: 'bottom bottom',
            scrub: 0.1,
            immediateRender: false,
            invalidateOnRefresh: true,
          },
        });

        tl.set(`.tc-1 > span`, {
          autoAlpha: 1,
        });

        tl.set(`.tc-2 > span`, {
          autoAlpha: 0,
          opacity: 0,
        });

        tl.fromTo(
          '.tc-1 > span',
          {
            autoAlpha: 1,
          },
          {
            autoAlpha: 0,
            duration: 0.35,
            stagger: 0.1,
          },
          0.2,
        );

        tl.fromTo(
          '.tc-2 > span',
          {
            autoAlpha: 0,
          },
          {
            autoAlpha: 1,
            duration: 0.35,
            stagger: 0.1,
          },
          '-=0.5',
        );

        tl.addPause(3);

        tl.fromTo(
          '.tc-2 > span',
          {
            autoAlpha: 1,
          },
          {
            autoAlpha: 0,
            duration: 0.2,
            stagger: 0.07,
          },
        );

        tl.fromTo(
          videoRef.current,
          {
            autoAlpha: 0,
          },
          {
            autoAlpha: 1,
            duration: 1,
          },
        );

        tl.addPause(5.5);
      });
    };

    const intersectionObserver = useRef<IntersectionObserver | null>(null);

    useIsomorphicLayoutEffect(() => {
      intersectionObserver.current = new IntersectionObserver(
        () => {
          animationInit();
        },
        {
          root: null,
          rootMargin: '100px',
          threshold: 0,
        },
      );

      intersectionObserver.current.observe(container.current as Element);

      return () => {
        if (intersectionObserver.current) {
          intersectionObserver.current.disconnect();
        }
      };
    }, []);

    return (
      <section
        className={className ? `${styles.root} ${className}` : styles.root}
        ref={container}
      >
        <div className={styles.stickyWrapper}>
          <Container
            style={{
              position: 'relative',
              height: '100%',
              display: 'grid',
              placeItems: 'center',
            }}
          >
            <PLargeGradient className={`${styles.textContent} tc-1`}>
              {splitForStagger(t('testChart.line1'), locale).map((part, index) =>
                part.isWord ? (
                  <span key={index}>{part.text}</span>
                ) : (
                  part.text
                ),
              )}
            </PLargeGradient>

            <PLargeGradient className={`${styles.textContent} tc-2`}>
              {splitForStagger(t('testChart.line2'), locale).map((part, index) =>
                part.isWord ? (
                  <span key={index}>{part.text}</span>
                ) : (
                  part.text
                ),
              )}
            </PLargeGradient>
            <div
              className="img-container"
              ref={videoRef}
              style={{ position: 'relative', height: '100%', width: '100%' }}
            >
              {/* two crops CSS-toggled: a width-based src swap picks the wrong crop on SSR */}
              <Image
                src="web_v2/blood-vision/blood-marker-chart.png"
                fill
                alt={t('testChart.imageAlt')}
                className={`${styles.chartImg} ${styles.imgDesktop}`}
              />
              <Image
                src="web_v2/blood-vision/blood-marker-chart-responsive.png"
                fill
                alt={t('testChart.imageAlt')}
                className={`${styles.chartImg} ${styles.imgMobile}`}
              />
            </div>
          </Container>
          {/* {Number(width) > 768 ? (
            <video autoPlay loop muted playsInline ref={videoRef}>
              <source
                src="https://public-web-assets-cfront.ultrahuman.com/bumper_metabolic.mp4"
                type="video/mp4"
              />
            </video>
          ) : (
            <video autoPlay loop muted playsInline ref={videoRef}>
              <source
                src="https://public-web-assets-cfront.ultrahuman.com/bumper_metabolic_mobile.mp4"
                type="video/mp4"
              />
            </video>
          )} */}
          {/* {width > 768 ? (
            <div style={{ background: 'yellow' }}>{width}</div>
          ) : (
            <div style={{ background: 'red' }}>{width}</div>
          )} */}
        </div>
      </section>
    );
};
